import { addDays } from 'date-fns'
import { prop, uniq } from 'ramda'
import z from 'zod'
import { EsignFormStructure, GenericEsignDocument } from './esign'
import { mobileOrPhoneType } from './generic'
import { Permission } from './roles'
import { FrontendUserSchema } from './userMembership'
import { getVendorName, VendorSchema } from './vendor'

export const SettlementTerms = ['30 Days', '60 Days', '90 Days', 'Other'] as const
export const PurchaseTimeframes = [
    'ASAP',
    'Within 2-3 Months',
    'Within 3-6 Months',
    "I'm in No Rush",
] as const
export const PropertyTypes = ['House', 'Townhouse', 'Unit', 'Land'] as const
export const PropertyStyles = [
    'Queenslander',
    'Post War Character',
    'Modern Contemporary',
    'Architecturally Designed',
    'Hamptons',
    'Brick and Tile',
    'Art Deco',
    'Renovator',
] as const
export const RenovationPreferences = [
    'Yes happy to undertake any kinds of renovation',
    'Yes but only minor cosmetic renovation',
    'No',
] as const
export const BedroomOptions = ['1-2', '2-3', '3-4', '4-5', '5+'] as const
export const BathroomOptions = ['1-2', '2-3', '3+', 'All bedrooms to have ensuite'] as const
export const CarParkOptions = [
    'Single Carport',
    'Double Carport',
    'Single Garage',
    'Double Garage',
    'Triple Garage',
    '4 Car Garage',
    'Basement Parking',
] as const
export const LotSizeOptions = [
    'Small lots under 405m2',
    '405m2 to 600m2',
    '600m2+',
    '801m2+',
    '1,000m2+',
    'No minimum requirement',
] as const
export const YesNoUnsure = ['Yes', 'No', 'Unsure'] as const

export const WishlistSchema = z.object({
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    email: z.string().email().max(254),
    signUpForUpdates: z.boolean().optional(),
    postalAddress: z.string().max(300).optional(),
    purchaseEntity: z.string().max(200).optional(),
    subjectToFIRB: z.enum(YesNoUnsure).optional(),
    budgetRange: z.string().max(100).optional(),
    settlementTerms: z.enum(SettlementTerms).optional(),
    purchaseTimeframe: z.enum(PurchaseTimeframes).optional(),
    contactPerson: z.string().max(200).optional(),
    preferredCommunication: z.array(z.enum(['Text', 'Phone Call', 'WhatsApp Chat', 'Email'])).optional(),
    bestContactTimes: z.string().max(200).optional(),
    propertyTypes: z.array(z.enum(PropertyTypes)).optional(),
    propertyStyles: z.array(z.enum(PropertyStyles)).optional(),
    renovationPreference: z.enum(RenovationPreferences).optional(),
    wantPool: z.enum(YesNoUnsure).optional(),
    bedrooms: z.enum(BedroomOptions).optional(),
    bathrooms: z.enum(BathroomOptions).optional(),
    carParks: z.enum(CarParkOptions).optional(),
    schoolsImportant: z.string().max(1000).optional(),
    detailedDescription: z.string().max(5000).optional(),
    targetSuburbs: z.string().max(500).optional(),
    dealBreakers: z.string().max(2000).optional(),
    preferredLotSize: z.enum(LotSizeOptions).optional(),
    specificProperties: z.string().max(2000).optional(),
    currentListingsInterest: z.string().max(2000).optional(),
    solicitorDetails: z.string().max(500).optional(),
    bankerBrokerDetails: z.string().max(500).optional(),
    financeDocumentUrl: z.string().max(2000).optional(),
})

export type WishlistSchema = z.infer<typeof WishlistSchema>

// Buyer-journey statuses (BuyerFlo design — 5-stage taxonomy).
// Legacy 7-status values are remapped by the data migration:
//   'active search' → 'searching'; 'offer submitted' + 'under contract' → 'under offer';
//   'settlement' + 'completed' → 'settled'; 'withdrawn' → 'lost'.
export const PurchaserStatuses = [
    'consultation',
    'searching',
    'under offer',
    'settled',
    'lost',
] as const
export type PurchaserStatus = (typeof PurchaserStatuses)[number]

export const GenericPersonSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    mobileNumber: mobileOrPhoneType,
})

export const UpdateAdditionalDetailsSchema = z.object({
    additionalDetails: z.record(z.string(), z.string().optional()),
})
export type UpdateAdditionalDetailsSchema = z.infer<typeof UpdateAdditionalDetailsSchema>

export const PurchaserAgentSchema = z.object({
    userId: z.string(),
    split: z.number().min(0).max(1),
})

export const CorePurchaserSchema = z
    .object({
        agents: z.array(PurchaserAgentSchema).min(1),
        buyers: z.array(VendorSchema).min(1),
        authorityStartDate: z.string().date().optional(),
        authorityPeriod: z.number().optional(),
        propertyAddress: z.string().optional(),
        additionalDetails: z.record(z.string(), z.string().optional()).optional(),
        commissionIncEx: z.enum(['inc', 'ex']).optional().default('inc'),
        commissionType: z.enum(['percentage', 'flat']).optional().default('percentage'),
        commitmentFee: z.number().optional(),
    })
    .refine(data => data.agents.reduce((acc, agent) => acc + agent.split, 0) == 1, {
        message: 'Agent splits must add up to 1',
    })

export const UpdatePurchaserSchema = CorePurchaserSchema.and(
    z.object({
        commission: z.number().optional(), // Inclusive
        purchaserStatus: z.enum(PurchaserStatuses).optional(),
        contractPrice: z.number().min(0).optional(),
        unconditionalDate: z.string().date().optional(),
        propertyAddress: z.string().optional(),
        wishlist: WishlistSchema.optional(),
    }),
)

export const NewPurchaserSchema = CorePurchaserSchema.and(
    z.object({
        purchaserStatus: z.enum(PurchaserStatuses).default('consultation'),
        commission: z.number(),
        wishlist: WishlistSchema.optional(),
    }),
)

export type UpdatePurchaserSchema = z.infer<typeof UpdatePurchaserSchema>

// Per-tab schemas for independent validation and tRPC handlers
export const UpdateClientTabSchema = z
    .object({
        agents: z.array(PurchaserAgentSchema).min(1),
        buyers: z.array(VendorSchema).min(1),
        authorityStartDate: z.string().date().optional(),
        authorityPeriod: z.number().optional(),
        commission: z.number().optional(),
        commissionIncEx: z.enum(['inc', 'ex']).optional().default('inc'),
        commissionType: z.enum(['percentage', 'flat']).optional().default('percentage'),
        commitmentFee: z.number().optional(),
        purchaserStatus: z.enum(PurchaserStatuses).optional(),
        additionalDetails: z.record(z.string(), z.string().optional()).optional(),
    })
    .refine(data => data.agents.reduce((acc, agent) => acc + agent.split, 0) == 1, {
        message: 'Agent splits must add up to 1',
    })
export type UpdateClientTabSchema = z.infer<typeof UpdateClientTabSchema>

export const UpdatePurchasedPropertyTabSchema = z.object({
    contractPrice: z.number().min(0).optional(),
    unconditionalDate: z.string().date().optional(),
    propertyAddress: z.string().optional(),
})
export type UpdatePurchasedPropertyTabSchema = z.infer<typeof UpdatePurchasedPropertyTabSchema>

export type NewPurchaserSchema = z.infer<typeof NewPurchaserSchema> & {
    statusLog: Record<PurchaserStatus, number> // timestamp on status change
}

export type InternalPurchaserSchema = Omit<NewPurchaserSchema, 'buyers'> & {
    orgId: string
    purchaserId: string
    lastUpdated: number
    additionalDetails: {
        [key: string]: string | null
    }
    buyers: VendorSchema[]
    storageName: string
    conjunctionalAgreements?: ConjunctionalAgreementSchema[]
    agentAppointmentForm?: EsignFormStructure
    genericEsignDocuments?: Record<string, GenericEsignDocument>
    wishlist?: WishlistSchema
    contractPrice?: number
    unconditionalDate?: string
    propertyAddress?: string
    engagementInvoice?: {
        xeroInvoiceId: string
        xeroStatus?: string // Xero invoice status: DRAFT, SUBMITTED, AUTHORISED, PAID, VOIDED, DELETED
        scheduledSendAt?: number // If set, invoice sending is scheduled for this time
        sendError?: string // If set, last send attempt failed with this error
    }
    commissionInvoice?: {
        xeroInvoiceId: string
        xeroStatus?: string // Xero invoice status: AUTHORISED, PAID, VOIDED, DELETED
        sendError?: string // If set, last send attempt failed with this error
    }
}

export const ownsPurchaser =
    (purchaser: { agents?: { userId: string }[] }) => (userId: string) =>
        purchaser.agents?.some(agent => agent.userId == userId)

export const ConjunctionalAgreementSchema = z
    .union([
        z.object({
            type: z.literal('external'),
            referrerName: z.string(),
        }),
        z.object({
            type: z.literal('internal'),
            agentId: z.string(),
        }),
    ])
    .and(
        z.union([
            z.object({
                agreementType: z.literal('fixed'),
                commissionAmount: z.number(),
            }),
            z.object({
                agreementType: z.literal('percentage'),
                commissionPercent: z.number(),
                commissionCap: z.number().optional(),
            }),
        ]),
    )

export const UpdateConjunctionalAgreementSchema = z.object({
    conjunctionalAgreements: z.array(ConjunctionalAgreementSchema),
})

export type ConjunctionalAgreementSchema = z.infer<typeof ConjunctionalAgreementSchema>
export type UpdateConjunctionalAgreementSchema = z.infer<typeof UpdateConjunctionalAgreementSchema>

export const purchaserEnabledForUser = (
    purchaser: InternalPurchaserSchema,
    usersData: FrontendUserSchema[] | undefined,
    user: FrontendUserSchema & { permissions: Permission[] },
) =>
    user.permissions.includes('purchasers.fullControl') ||
    (user.permissions.includes('purchasers.edit') &&
        (purchaser.agents.map(prop('userId')).includes(user.userId) ||
            purchaser.agents.map(prop('userId')).includes(user.salesAssistantLeadAgentId) ||
            (user.salesAssistantConjunctionalAgencyId != undefined &&
                usersData?.find(x => purchaser.agents.map(prop('userId')).includes(x.userId))
                    ?.conjunctionalAgencyId == user.salesAssistantConjunctionalAgencyId)))

export const getClientNames = (buyers: VendorSchema[] | undefined, separator = ' & ') =>
    buyers?.map(getVendorName).join(separator) ?? ''

/**
 * End of the agent authority period (Form 6 expiry). Inclusive, so a 90-day
 * authority starting on the 1st ends on day 90 rather than day 91. `undefined`
 * when either half of the period is unset.
 */
export const getAuthorityEndDate = (purchaser: {
    authorityStartDate?: string
    authorityPeriod?: number
}): Date | undefined =>
    purchaser.authorityStartDate && purchaser.authorityPeriod
        ? addDays(new Date(purchaser.authorityStartDate), purchaser.authorityPeriod - 1)
        : undefined

/**
 * Filter/sort state for the clients list. The list is fetched whole
 * (`purchaser.getPurchasers`) and narrowed in the browser, so none of this ever
 * reaches the server.
 */
export type PurchaserListFilters = {
    search: string
    status: PurchaserStatus[]
    agentIds: string[]
    actionFilters: Record<string, string[]>
    /** Column to sort by — see `purchaserSortValue`. Unset leaves the list unsorted. */
    sort?: string
    sortDirection: 'asc' | 'desc'
}

const sanitizeForEmail = (str: string) => str.toLowerCase().replace(/[^a-z0-9.-]/g, '')

export const getStorageName = async (orgName: string, buyers: VendorSchema[]) => {
    const distinctNames = uniq(
        buyers.map(buyer =>
            buyer.vendorType === 'company'
                ? sanitizeForEmail(buyer.companyName)
                : sanitizeForEmail(buyer.lastName),
        ),
    )
    return `${distinctNames.join('-')}-${sanitizeForEmail(orgName)}`
}
